import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Reveal } from "@/components/KineticLines";

const inputCls = "rounded-lg w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-primary transition-colors duration-300";

export const QuoteForm = ({ variant }) => {
  const isCommercial = variant === "commercial";
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", address: "", location: "", service: "", size: "", timeline: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/quotes", { ...form, type: variant });
      toast.success("Request received! We'll be in touch within one business day.");
      setForm({ name: "", email: "", phone: "", company: "", address: "", location: "", service: "", size: "", timeline: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" data-testid={`quote-section-${variant}`} className="py-24 md:py-36 px-5 md:px-10 border-t border-white/10">
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 md:gap-20">
        <Reveal>
          <p className="text-primary text-xs font-bold tracking-[0.3em] uppercase mb-3">{isCommercial ? "Request a Bid" : "Free Estimate"}</p>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.9]">
            {isCommercial ? <>LET'S PRICE<br />YOUR <span className="text-primary">PROJECT</span></> : <>GET YOUR<br />FREE <span className="text-primary">QUOTE</span></>}
          </h2>
          <p className="mt-6 text-white/60 text-sm md:text-base max-w-md leading-relaxed">
            {isCommercial
              ? "Send us the basics and we'll turn around a detailed proposal. Have drawings ready? Email them straight to our estimating team."
              : "Tell us about your home and we'll come out, measure, and give you a straight answer on price. No pressure, no games."}
          </p>
          <div className="mt-8 space-y-3 text-sm">
            <p className="text-white/80"><span className="text-primary font-bold">CALL</span> <a href="tel:+12033075222" data-testid="quote-phone-link" className="ml-3 hover:text-primary transition-colors duration-300">(203) 307-5222</a></p>
            <p className="text-white/80"><span className="text-primary font-bold">EMAIL</span> <a href="mailto:yali@infinitesiding.com" data-testid="quote-email-link" className="ml-3 hover:text-primary transition-colors duration-300">yali@infinitesiding.com</a></p>
            <p className="text-white/80"><span className="text-primary font-bold">HOURS</span> <span className="ml-3 text-white/60">Mon – Sat · 7:00 AM – 8:00 PM</span></p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <form onSubmit={submit} data-testid={`quote-form-${variant}`} className="space-y-4">
            {isCommercial && (
              <input required value={form.company} onChange={set("company")} placeholder="Company / GC name *" data-testid="quote-company-input" className={inputCls} />
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input required value={form.name} onChange={set("name")} placeholder={isCommercial ? "Contact name *" : "Your name *"} data-testid="quote-name-input" className={inputCls} />
              <input required type="email" value={form.email} onChange={set("email")} placeholder="Email *" data-testid="quote-email-input" className={inputCls} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input value={form.phone} onChange={set("phone")} placeholder="Phone" data-testid="quote-phone-input" className={inputCls} />
              {isCommercial ? (
                <input value={form.location} onChange={set("location")} placeholder="Project location (NY / CT / MA)" data-testid="quote-location-input" className={inputCls} />
              ) : (
                <input value={form.address} onChange={set("address")} placeholder="Home address / town" data-testid="quote-address-input" className={inputCls} />
              )}
            </div>
            {isCommercial ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input value={form.size} onChange={set("size")} placeholder="Approx. square footage" data-testid="quote-size-input" className={inputCls} />
                <select value={form.timeline} onChange={set("timeline")} data-testid="quote-timeline-select" className={`${inputCls} appearance-none`}>
                  <option value="" className="bg-neutral-900">Target start date</option>
                  <option value="ASAP" className="bg-neutral-900">ASAP</option>
                  <option value="1-3 months" className="bg-neutral-900">1–3 months</option>
                  <option value="3-6 months" className="bg-neutral-900">3–6 months</option>
                  <option value="Planning" className="bg-neutral-900">Just planning</option>
                </select>
              </div>
            ) : (
              <select value={form.service} onChange={set("service")} data-testid="quote-service-select" className={`${inputCls} appearance-none`}>
                <option value="" className="bg-neutral-900">What do you need?</option>
                <option value="New siding installation" className="bg-neutral-900">New siding installation</option>
                <option value="Siding replacement" className="bg-neutral-900">Siding replacement</option>
                <option value="Siding repair / storm damage" className="bg-neutral-900">Siding repair / storm damage</option>
                <option value="Custom metal pans / waterproofing" className="bg-neutral-900">Custom metal pans, caulking & waterproofing</option>
                <option value="Building maintenance" className="bg-neutral-900">Building maintenance — siding repair, pressure washing, window cleaning</option>
                <option value="Addition / deck / roofing / paint" className="bg-neutral-900">Addition, deck, roofing or paint</option>
                <option value="Something else" className="bg-neutral-900">Something else</option>
              </select>
            )}
            <textarea rows={4} value={form.message} onChange={set("message")} placeholder={isCommercial ? "Project details, scope, plans status..." : "Tell us about your project..."} data-testid="quote-message-input" className={`${inputCls} resize-none`} />
            <button
              type="submit"
              disabled={sending}
              data-testid="quote-submit-button"
              className="rounded-lg w-full bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm py-4 flex items-center justify-center gap-3 transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(193,214,46,0.35)] disabled:opacity-60 disabled:translate-y-0"
            >
              {sending ? "Sending..." : (isCommercial ? "Request Bid" : "Request Free Quote")} <Send size={16} aria-hidden="true" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
